import type { PersonColor } from '@shh/design-tokens';
import { cn } from '../lib/cn';
import { personSolid } from '../lib/person';

export type AvatarSize = 'sm' | 'md' | 'lg' | 'xl';

const sizes: Record<AvatarSize, string> = {
  sm: 'size-avatar-sm text-sm',
  md: 'size-avatar-md text-md',
  lg: 'size-avatar-lg text-2xl',
  xl: 'size-avatar-xl text-3xl',
};

export interface AvatarProps {
  name: string;
  color: PersonColor;
  size?: AvatarSize;
  /** Optional emoji shown instead of the initial (kids love picking their own). */
  emoji?: string;
  /** Set when the name is already visible next to the avatar, so it is not read twice. */
  decorative?: boolean;
  className?: string;
}

/** First grapheme, so names like "Özlem" or emoji-prefixed names stay intact. */
function initialOf(name: string): string {
  const segmenter = new Intl.Segmenter('de', { granularity: 'grapheme' });
  const first = segmenter.segment(name.trim())[Symbol.iterator]().next().value;
  return first ? first.segment.toLocaleUpperCase('de') : '?';
}

/**
 * Person avatar. Colour is never the only identifier: the initial (or emoji) is always shown
 * and the name is exposed to assistive tech.
 */
export function Avatar({ name, color, size = 'md', emoji, decorative, className }: AvatarProps) {
  return (
    <span
      role={decorative ? undefined : 'img'}
      aria-label={decorative ? undefined : name}
      aria-hidden={decorative || undefined}
      className={cn(
        'inline-grid shrink-0 place-items-center rounded-avatar font-display font-bold leading-none select-none',
        personSolid[color],
        sizes[size],
        className,
      )}
    >
      {emoji ?? initialOf(name)}
    </span>
  );
}
